import React, { lazy, Suspense } from 'react';
import { RouterProvider, createBrowserRouter, Navigate, useParams } from 'react-router-dom';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import * as Sentry from '@sentry/react';
import ErrorBoundary from './components/common/ErrorBoundary';
import { APP_AR, APP_DEMO, APP_DEMO_ITB } from './constants/apps';

function LegacyRedirect() {
  const { slug, '*': rest } = useParams();
  const segments = (rest || '').split('/');
  const lastId = segments[segments.length - 1];
  return <Navigate to={`/${slug}/${lastId}`} replace />;
}

import viteStore from './redux/store'
import HomeSkeleton from './components/skeletons/HomeSkeleton'
import ErrorPage from './pages/ErrorPage';
import LoadingPage from './pages/LoadingPage';
import DynamicPlacePage from './components/factories/DynamicPage';
import { FontSizeProvider } from './contexts/FontSizeContext';
import { AuthProvider } from './contexts/AuthContext';

import { setupGlobalErrorHandlers } from './utils/errorHandler';
import { MAPBOX_RTL_PLUGIN_URL } from './config';
import mapboxgl from 'mapbox-gl';

import './index.css'

Sentry.init({
  dsn: "https://d853c5e0b56ad58fc6505b51239749d7@o4510659917512704.ingest.de.sentry.io/4510992895770704",
  sendDefaultPii: true,
  environment: import.meta.env.MODE,
  enabled: import.meta.env.PROD,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
  ],
  // Errors whose innermost frame lives on one of our *page* URLs rather than
  // under /assets/ come from scripts injected into the page — Chrome iOS and
  // the Google app inject Google Translate via evaluateJavaScript, and WebKit
  // attributes that code to the document URL. Their internal recursion
  // ("Maximum call stack size exceeded", CUSTOM-APP-2A/2B/2C/2D) is not ours
  // to fix. Our own bundle is always served from /assets/, so it still reports.
  denyUrls: [/^https:\/\/app\.xsite\.com\/(?!assets\/)/],
  ignoreErrors: [
    /Invalid LngLat object/,
    /Can't find variable: EmptyRanges/,
    /The object can not be found here/,
    /NotFoundError.*removeChild/,
    /NotFoundError.*insertBefore/,
    // Benign user/platform denials: media autoplay blocked without a user
    // gesture (react-player play() on iOS) and denied geolocation permission.
    // Handled gracefully; nothing actionable.
    /NotAllowedError/,
    /runtime\.sendMessage\(\)/,
    /Fetch is aborted/,
    /Indexed Database server/,
    /^TypeError: Load failed$/,
    /^TypeError: Failed to fetch$/,
    /^TypeError: cancelled$/,
    /^TypeError: NetworkError when attempting to fetch resource\.$/,
    /Unable to preload CSS/,
    // A lazy() chunk that failed to load — Chrome's, Safari's and Firefox's
    // wording for the same thing (network blip, or a stale index after a
    // deploy); setupGlobalErrorHandlers reloads the page once to recover.
    /Failed to fetch dynamically imported module/,
    /Importing a module script failed/,
    /error loading dynamically imported module/,
    /Failed to get document because the client is offline/,
    /The I\/O read operation failed/,
    /Event `CustomEvent` \(type=unhandledrejection\)/,
    /browser\.storage\.local\.set\(\)/,
  ],
  tracesSampleRate: 0.2,
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
});

if (!mapboxgl.getRTLTextPluginStatus || 
    mapboxgl.getRTLTextPluginStatus() === 'unavailable' || 
    mapboxgl.getRTLTextPluginStatus() !== 'loaded') {
    mapboxgl.setRTLTextPlugin(MAPBOX_RTL_PLUGIN_URL, null, true);
}

const AppLayout = lazy(() => import('./AppLayout'))
const HomePage = lazy(() => import('./pages/HomePage'))
const AboutPage = lazy(() => import('./pages/AboutPage'))
const Keypad = lazy(() => import('./components/common/keypad/Keypad'))
const InfoPage = lazy(() => import('./pages/info/InfoPageFactory'))
const ARHome = lazy(() => import('./pages/ar/ARHomePage'))
const ARScene = lazy(() => import('./pages/ar/Scene'))
const GatePage = lazy(() => import('./pages/GatePage'))
const GateWrapper = lazy(() => import('./components/auth/GateWrapper'))
const Map = lazy(() => import('./components/map/Map'))

const Demo = lazy(() => import('./pages/demo/DemoPage'))
const Itb = lazy(() => import('./pages/itb/ItbDemo'))
const IntroPage = lazy(() => import('./pages/IntroPage'))

function setVh(): void {
  let vh = window.innerHeight * 0.01;
  document.documentElement.style.setProperty('--vh', `${vh}px`);
}

setupGlobalErrorHandlers();
setVh();
window.addEventListener('resize', setVh);
const slug = window.location.pathname.split('/')[1]

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

if (slug === APP_AR) {
  const router = createBrowserRouter([
    {
      element: (
        <Suspense fallback={<LoadingPage />}>
          <ARHome />
        </Suspense>
      ),
      path: `/${APP_AR}`,
    },
    {
      element: (
        <Suspense fallback={<LoadingPage />}>
          {/* @ts-ignore - modelName comes from route params */}
          <ARScene />
        </Suspense>
      ),
      path: `${APP_AR}/scene/:modelName`
    }
  ])

  root.render(
    <Provider store={viteStore}>
      <RouterProvider router={router} />
    </Provider>
  )
} else if (slug === APP_DEMO) {
  root.render(
    <Demo />
  )
} else if (slug === APP_DEMO_ITB) {
  root.render(
    <Itb />
  )
} 
else {
  const router = createBrowserRouter([
    {
      element: (
        <Suspense fallback={<LoadingPage />}>
          <AuthProvider>
            <AppLayout />
          </AuthProvider>
        </Suspense>
      ),
      errorElement: <ErrorPage />,
      path: ':slug',
      children: [
        {
          element: (
            <Suspense fallback={<HomeSkeleton />}>
              <HomePage />
            </Suspense>
          ),
          index: true
        },
        { element: (<GateWrapper><ErrorBoundary><Map type={'main'} /></ErrorBoundary></GateWrapper>), path: 'map' },
        {
          element: (
            <GateWrapper>
              <ErrorBoundary>
                <IntroPage />
              </ErrorBoundary>
            </GateWrapper>
          ),
          path: 'intro'
        },
        { 
          element: (
            <GateWrapper>
              <ErrorBoundary>
                <AboutPage />
              </ErrorBoundary>
            </GateWrapper>
          ), 
          path: 'about/:placeType/:aboutPlaceId' 
        },
        { element: <ErrorBoundary><Keypad /></ErrorBoundary>, path: 'keypad' },
        {
          element: <ErrorBoundary><InfoPage /></ErrorBoundary>,
          path: 'info/:infoType'
        },
        { 
          element: (
            <GateWrapper>
              <ErrorBoundary>
                <DynamicPlacePage />
              </ErrorBoundary>
            </GateWrapper>
          ), 
          path: ':placeId' 
        },
        {
          element: <LegacyRedirect />,
          path: '*'
        }
      ]
    },
    {
      element: (
        <Suspense fallback={<LoadingPage />}>
          <AuthProvider>
            <GatePage />
          </AuthProvider>
        </Suspense>
      ),
      path: ':slug/gate',
      errorElement: <ErrorPage />
    }
  ])

  root.render(
    <FontSizeProvider>
      <Provider store={viteStore}>
        <ErrorBoundary>
          <Suspense fallback={<LoadingPage />}>
            <RouterProvider router={router} />
          </Suspense>
        </ErrorBoundary>
      </Provider>
    </FontSizeProvider>
  )
}
